import { createBrowserRouter, Navigate } from 'react-router';

import { REWARDS_NAME_PLURAL } from 'constants/rewards';
import {
  GET_STARTED_PATH,
  INTERNAL_PATH,
  LOCATION_HOME_PATH,
  ONBOARDING_PATH,
  ONBOARDING_ROUTE,
  CHANNEL_INSIGHTS_PATH,
  REPORTS_PATH,
  THIRD_PARTY_ORDERS_PATH,
  WEBSITE_ACCESS_PATH,
  TRANSACTION_FEES_PATH,
} from 'constants/path';
import { ERROR_PAGE_TITLES, ERROR_PAGE_SUBTITLES, ERROR_PAGE_BUTTONS } from 'constants/error';
import { GO_LIVE_COVER_TITLE } from 'constants/locationGoLive';
import { LOCATION_SETTINGS } from 'constants/locationSettings';
import { ORDER, ADJUSTMENT, CANCELLATIONS, CONTROL, POS_ERROR } from 'constants/reporting';

import { capitalize } from 'helpers/utils';
import { getCompanyRedirectPath } from 'helpers/url';
import { isSelfServeOnboardingLink } from 'helpers/selfServeOnboarding';
import { lazyRoute, lazyRouteWithProps } from 'helpers/lazyRoute';
import { PROTECTED_LAYOUT_ROUTE_ID } from 'helpers/routeMatches';

import { CHANNEL_MANAGER_TABS } from 'containers/ChannelManager/constants';

import * as SCOPES from 'scopes/nav';

// Route containers are loaded with react-router's `lazy` so they ship in their own chunk.
// Only the app shell and the `/menu` subtree (owned by CN-51661) are imported statically.
import { ONBOARDING_STEPS } from 'containers/OnboardingWizard/steps';
import { FIRST_GET_STARTED_SCREEN, GET_STARTED_SCREENS } from 'containers/GetStarted/screens';
import { ONBOARDING_SOURCE } from 'constants/analytics';

import ManageMenus from 'containers/ManageMenus/ManageMenus';
import MenuEditCreate from 'containers/MenuEditCreate/MenuEditCreate';
import PreviewMenu from 'containers/PreviewMenu/PreviewMenu';
import MenuManagementLayout from 'layouts/MenuManagementLayout';

import ErrorPage from 'components/Errors/ErrorPage/ErrorPage';
import AuthLayout from 'layouts/AuthLayout';
import Layout from 'layouts/Layout';
import { OnboardingLockIn } from 'layouts/OnboardingLockIn';
import ProtectedLayout from 'layouts/ProtectedLayout';
import { ProtectedRouteWrapper } from 'layouts/ProtectedRouteWrapper';

import { dimension } from 'styles/theme.variables';

const reportingRoute = props =>
  lazyRouteWithProps(() => import('layouts/ReportingLayout'), 'default', props);

const embeddedAdminRoute = props =>
  lazyRouteWithProps(() => import('components/EmbeddedAdmin/EmbeddedAdmin'), 'default', props);

const locationDetailsRoute = activeTab =>
  lazyRouteWithProps(() => import('containers/Locations/LocationDetails'), 'default', {
    activeTab,
  });

const channelManagerRoute = activeTab =>
  lazyRouteWithProps(() => import('containers/ChannelManager/ChannelManager'), 'ChannelManager', {
    activeTab,
  });

export const orderDetailLinks = [
  {
    path: ':orderId',
    handle: {
      name: 'Order Details',
      analyticsEventName: 'view_orders_detail',
      key: SCOPES.ORDER_DETAILS,
    },
    children: [
      { index: true, lazy: lazyRoute(() => import('containers/OrderDetails/OrderDetails')) },
      {
        path: 'partial-refunds',
        lazy: lazyRoute(() => import('containers/PartialRefunds/PartialRefunds')),
        handle: { name: 'Refund by Item', analyticsEventName: 'view_orders_refund_by_item' },
      },
      {
        path: 'partial-refund-details',
        lazy: lazyRoute(
          () => import('containers/PartialRefundPage/PartialRefundPage'),
          'PartialRefundPage',
        ),
        handle: { name: 'Refund by Item' },
      },
    ],
  },
];

export const orderLinks = [
  {
    path: '',
    handle: {
      name: 'ChowNow Orders',
      analyticsEventName: 'view_orders_chownow',
      key: SCOPES.DIRECT_ORDERS,
    },
    children: [
      {
        index: true,
        lazy: lazyRoute(() => import('containers/OrdersList/OrdersListDirect')),
      },
      ...orderDetailLinks,
    ],
  },
  {
    path: THIRD_PARTY_ORDERS_PATH,
    lazy: reportingRoute({ title: 'Third Party Orders', reportType: ORDER }),
    handle: {
      name: 'Third Party Orders',
      analyticsEventName: 'view_orders_third_party',
      key: SCOPES.THIRD_PARTY_ORDERS,
    },
  },
];

export const locationBasedOrderLinks = [
  {
    index: true,
    handle: {
      name: 'ChowNow Orders',
      analyticsEventName: 'view_orders_chownow',
      key: SCOPES.DIRECT_ORDERS,
      hideForHub: true,
    },
    lazy: lazyRoute(() => import('containers/OrdersList/OrdersListDirect')),
  },
  {
    path: THIRD_PARTY_ORDERS_PATH,
    lazy: reportingRoute({ title: 'Third Party Orders', reportType: ORDER }),
    handle: {
      name: 'Third Party Orders',
      analyticsEventName: 'view_orders_third_party',
      key: SCOPES.THIRD_PARTY_ORDERS_LOCATION_BASED,
    },
  },
];

export const reportingLinks = [
  {
    path: 'analytics',
    lazy: lazyRoute(() => import('containers/Reporting/Analytics')),
    handle: {
      name: 'Analytics',
      analyticsEventName: 'view_reports_analytics',
      key: SCOPES.ANALYTICS,
    },
  },
];

export const cubohReportingLinks = [
  {
    path: 'items',
    lazy: lazyRoute(() => import('containers/Reporting/ItemReport'), 'ItemReport'),
    handle: {
      name: 'Item Report',
      analyticsEventName: 'view_reports_item',
      key: SCOPES.ITEM_REPORT,
    },
  },
  {
    path: 'modifiers',
    lazy: lazyRoute(() => import('containers/Reporting/ModifierReport'), 'ModifierReport'),
    handle: {
      name: 'Modifier Report',
      analyticsEventName: 'view_reports_modifier',
      key: SCOPES.MODIFIER_REPORT,
    },
  },
  {
    path: 'order-history',
    lazy: reportingRoute({ title: 'Order History', reportType: ORDER }),
    handle: {
      name: 'Order History',
      analyticsEventName: 'view_reports_order_history',
      key: SCOPES.ORDER_HISTORY,
    },
  },
  {
    path: 'order-edits',
    lazy: reportingRoute({ title: 'Order Edit Data', reportType: ADJUSTMENT }),
    handle: {
      name: 'Order Edit Data',
      analyticsEventName: 'view_reports_order_edit_data',
      key: SCOPES.ORDER_EDIT_DATA,
    },
  },
  {
    path: 'order-cancellations',
    lazy: reportingRoute({ title: 'Cancellation Data', reportType: CANCELLATIONS }),
    handle: {
      name: 'Cancellation Data',
      analyticsEventName: 'view_reports_cancellation_data',
      key: SCOPES.ORDER_CANCELLATIONS,
    },
  },
  {
    path: 'order-control',
    lazy: reportingRoute({ title: 'Order Control Data', reportType: CONTROL }),
    handle: {
      name: 'Order Control Data',
      analyticsEventName: 'view_reports_order_control_data',
      key: SCOPES.ORDER_CONTROL_DATA,
    },
  },
  {
    path: 'pos-errors',
    lazy: reportingRoute({ title: 'POS Errors', reportType: POS_ERROR }),
    handle: {
      name: 'POS Errors',
      analyticsEventName: 'view_reports_pos_errors',
      key: SCOPES.POS_ERRORS,
    },
  },
];

export const cnOnlyReportingLinks = [
  {
    path: 'accounting',
    lazy: embeddedAdminRoute({
      src: 'report/disbursements_v2',
      title: 'Accounting Data',
      navKey: SCOPES.ACCOUNTING_DATA,
    }),
    handle: {
      name: 'Accounting Data',
      analyticsEventName: 'view_reports_accounting_data',
      key: SCOPES.ACCOUNTING_DATA,
    },
  },
  {
    path: 'customer-spending',
    lazy: lazyRoute(() => import('containers/Reporting/CustomerReport')),
    handle: {
      name: 'Customer Data',
      analyticsEventName: 'view_reports_customer_data',
      key: SCOPES.CUSTOMER_DATA,
    },
  },
  {
    path: 'emailed',
    lazy: lazyRoute(() => import('containers/Reporting/EmailedReport')),
    handle: { name: 'Emailed', analyticsEventName: 'view_reports_emailed', key: SCOPES.EMAILED },
  },
  {
    path: 'promos',
    lazy: embeddedAdminRoute({
      src: 'report/promotions',
      title: 'Promo Data',
      navKey: SCOPES.PROMO_DATA,
    }),
    handle: {
      name: 'Promo Data',
      analyticsEventName: 'view_reports_promo_data',
      key: SCOPES.PROMO_DATA,
    },
  },
  {
    path: 'order-source',
    lazy: embeddedAdminRoute({ src: 'report/transaction_source', title: 'Order Source' }),
    handle: {
      name: 'Order Source',
      analyticsEventName: 'view_reports_order_source',
      key: SCOPES.ORDER_SOURCE,
    },
  },
  {
    path: 'order-overview',
    lazy: embeddedAdminRoute({
      src: 'report/orders',
      title: 'Order Overview',
      navKey: SCOPES.ORDER_OVERVIEW,
    }),
    handle: {
      name: 'Order Overview',
      analyticsEventName: 'view_reports_order_overview',
      key: SCOPES.ORDER_OVERVIEW,
    },
  },
];

// Order matches the location Reports nav
export const locationReportingLinks = [
  ...reportingLinks,
  ...cnOnlyReportingLinks,
  ...cubohReportingLinks,
];

// The `menu` subtree stays on static `element:` imports — CN-51661 owns it.
export const menuLinks = [
  {
    path: 'menu',
    handle: {
      name: 'Menu Editor',
      analyticsEventName: 'view_menu_editor',
      key: SCOPES.MANAGE_MENU,
    },
    element: <MenuManagementLayout />,
    children: [
      {
        index: true,
        element: <ManageMenus />,
      },
      {
        path: ':tab',
        element: <ManageMenus />,
      },
      {
        path: 'edit',
        element: <MenuEditCreate />,
        handle: { name: 'Edit Menu', analyticsEventName: 'view_menu_edit' },
      },
      {
        path: 'create',
        element: <MenuEditCreate />,
        handle: { name: 'Create Menu', analyticsEventName: 'view_menu_create' },
      },
      {
        path: 'preview',
        element: <PreviewMenu />,
        handle: { name: 'Preview Menu', analyticsEventName: 'view_menu_preview' },
      },
    ],
  },
  {
    path: 'mapping',
    lazy: lazyRoute(() => import('containers/Mapping/Mapping')),
    handle: { name: 'Menu Mapping', analyticsEventName: 'view_menu_mapping', key: SCOPES.MAPPING },
  },
  {
    path: 'item-availability',
    lazy: lazyRoute(() => import('containers/ItemAvailability/ItemAvailability')),
    handle: {
      name: 'Item Availability',
      analyticsEventName: 'view_menu_item_availability',
      key: SCOPES.ITEM_AVAILABILITY,
    },
  },
  {
    path: 'manage-photos',
    lazy: lazyRoute(() => import('containers/ManagePhotos/ManagePhotos')),
    handle: {
      name: 'Photo Editor',
      analyticsEventName: 'view_menu_photo_editor',
      key: SCOPES.MANAGE_PHOTOS,
    },
  },
  {
    path: 'pos-menu-settings',
    lazy: lazyRoute(
      () => import('containers/PosMenuSettings/PosMenuSettingsLayout'),
      'PosMenuSettingsLayout',
    ),
    handle: {
      name: 'POS Menu Settings',
      analyticsEventName: 'view_pos_menu_settings',
      key: SCOPES.POS_MENU_SETTINGS,
    },
    children: [
      {
        index: true,
        lazy: lazyRoute(
          () => import('containers/PosMenuSettings/PosMenuSettings'),
          'PosMenuSettings',
        ),
      },
      {
        path: 'edit',
        element: <MenuEditCreate />,
        handle: {
          name: 'Edit Menu',
          analyticsEventName: 'view_pos_menu_edit',
          key: SCOPES.POS_MENU_SETTINGS,
        },
      },
      {
        path: 'preview',
        element: <PreviewMenu />,
        handle: {
          name: 'Preview Menu',
          analyticsEventName: 'view_pos_menu_preview',
          key: SCOPES.POS_MENU_SETTINGS,
        },
      },
    ],
  },
];

export const locationSettingsLinks = [
  {
    index: true,
    handle: {
      name: 'Location Details',
      analyticsEventName: 'view_location_details',
      key: SCOPES.LOCATIONS,
    },
    lazy: lazyRoute(() => import('containers/Locations/LocationDetails')),
  },
  {
    path: 'temp-closures',
    lazy: lazyRoute(() => import('containers/Closures/Closures')),
    handle: {
      name: 'Closures',
      analyticsEventName: 'view_location_closures',
      key: SCOPES.TEMP_CLOSURES,
    },
  },
  {
    path: 'payment-processing',
    handle: {
      name: 'Payment Settings',
      analyticsEventName: 'view_location_payment_settings',
      key: SCOPES.PAYMENT_PROCESSING,
    },
    lazy: lazyRoute(() => import('containers/PaymentProcessing/PaymentProcessing')),
  },
  {
    path: 'catering',
    handle: {
      name: 'Edit Catering',
      analyticsEventName: 'view_location_edit_catering',
      parent: 'Location Details',
    },
    lazy: lazyRoute(
      () => import('containers/CateringSettings/CateringSettingsForm'),
      'CateringSettingsForm',
    ),
  },
  {
    path: WEBSITE_ACCESS_PATH,
    lazy: lazyRoute(() => import('containers/WebsiteAccess/WebsiteAccess'), 'WebsiteAccess'),
    handle: {
      name: 'Website Access',
      analyticsEventName: 'view_location_website_access',
      key: SCOPES.WEBSITE_ACCESS,
    },
  },
  {
    path: 'customizations',
    handle: {
      name: 'Customizations',
      analyticsEventName: 'view_location_customizations',
      key: SCOPES.CUSTOMIZATIONS,
    },
    lazy: lazyRoute(() => import('containers/Customizations')),
  },
  {
    path: 'email-notifications',
    lazy: lazyRoute(() => import('containers/EmailNotifications')),
    handle: {
      name: 'Email Notifications',
      analyticsEventName: 'view_location_email_notifications',
      key: SCOPES.EMAIL_NOTIFICATIONS,
    },
  },
  {
    path: 'add-ons',
    lazy: lazyRoute(() => import('containers/AddOns')),
    handle: { name: 'Add-Ons', analyticsEventName: 'view_location_add_ons', key: SCOPES.ADD_ONS },
  },
];

export const integrationsLinks = [
  {
    path: 'orders-control',
    lazy: lazyRoute(() => import('containers/OrdersControl/OrdersControl')),
    handle: {
      name: 'Orders Control',
      analyticsEventName: 'view_orders_control',
      key: SCOPES.ORDERS_CONTROL,
    },
  },
  {
    path: 'integrations',
    handle: {
      name: 'Integrations',
      analyticsEventName: 'view_integrations',
      key: SCOPES.CUBOH_INTEGRATIONS,
    },
    children: [
      {
        path: 'pos/oauth/result/:app/:status',
        lazy: lazyRoute(() => import('components/Integrations/Result/PosOauthConnectionResult')),
      },
      {
        path: 'pos/oauth/:app/:status',
        lazy: lazyRoute(() => import('containers/Integrations/PosOauth')),
      },
      {
        path: ':action/:app',
        lazy: lazyRoute(() => import('containers/Integrations/Integrations'), 'Integrations'),
      },
      {
        path: '',
        lazy: lazyRoute(() => import('containers/Integrations/Integrations'), 'Integrations'),
      },
    ],
  },
  {
    path: 'pos-integrations',
    handle: {
      name: 'Integrations',
      analyticsEventName: 'view_integrations_cn',
      key: SCOPES.CN_POS_INTEGRATIONS,
    },
    children: [
      {
        index: true,
        lazy: lazyRoute(
          () => import('containers/PosIntegrations/PosIntegrationsRedirect'),
          'PosIntegrationsRedirect',
        ),
      },
      {
        path: 'square',
        handle: { name: 'Square Integration', analyticsEventName: 'view_integrations_square' },
        lazy: lazyRoute(() => import('containers/PosIntegrations/SquarePos/SquarePosLanding')),
      },
      {
        path: 'toast',
        handle: { name: 'Toast Integration', analyticsEventName: 'view_integrations_toast' },
        lazy: lazyRoute(() => import('containers/PosIntegrations/ToastPos/ToastPosLanding')),
      },
    ],
  },
];

export const companyLinks = [
  {
    path: 'orders',
    children: orderLinks,
  },
  {
    path: '',
    handle: { name: 'HQ Details', analyticsEventName: 'view_hq_details', key: SCOPES.HQ_DETAILS },
    children: [
      {
        index: true,
        lazy: lazyRoute(() => import('containers/HqDetails/HqDetails')),
      },
      {
        path: 'edit',
        handle: { name: 'Edit HQ Details', analyticsEventName: 'view_hq_edit_details' },
        lazy: lazyRoute(() => import('containers/EditHqDetails/EditHqDetails')),
      },
    ],
  },
  {
    path: 'users',
    lazy: lazyRoute(() => import('containers/HqUsers')),
    handle: { name: 'Users', analyticsEventName: 'view_hq_users', key: SCOPES.USERS },
  },
  {
    path: 'notifications',
    lazy: lazyRoute(() => import('containers/PushNotifications')),
    handle: {
      name: 'Push Notifications',
      analyticsEventName: 'view_marketing_push_notifications',
      key: SCOPES.PUSH_NOTIFICATIONS,
    },
  },
];

export const marketingLinks = [
  {
    path: 'campaign-insights',
    handle: {
      name: 'Campaign Insights',
      analyticsEventName: 'view_marketing_campaign_insights',
      key: SCOPES.CAMPAIGN_INSIGHTS,
    },
    children: [
      {
        index: true,
        lazy: lazyRoute(
          () => import('containers/CampaignInsights/CampaignInsights'),
          'CampaignInsights',
        ),
      },
      {
        path: 'campaign-settings',
        handle: {
          name: 'Campaign Settings',
          analyticsEventName: 'view_marketing_campaign_settings',
          key: SCOPES.CAMPAIGN_SETTINGS,
        },
        children: [
          {
            index: true,
            lazy: lazyRoute(
              () => import('containers/CampaignSettings/CampaignSettings'),
              'CampaignSettings',
            ),
          },
          {
            path: 'import-history',
            handle: {
              name: 'Import History',
              analyticsEventName: 'view_marketing_import_history',
              key: SCOPES.CAMPAIGN_SETTINGS,
            },
            children: [
              {
                index: true,
                lazy: lazyRoute(
                  () => import('containers/CampaignSettings/ImportHistory/ImportHistory'),
                  'ImportHistory',
                ),
              },
              {
                path: ':importId',
                handle: {
                  name: 'CSV Details',
                  analyticsEventName: 'view_marketing_csv_details',
                },
                lazy: lazyRoute(
                  () => import('containers/CampaignSettings/ImportHistory/CsvDetail/CsvDetail'),
                  'CsvDetail',
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'auto-campaigns',
    lazy: lazyRoute(() => import('containers/AutoCampaigns/AutoCampaigns'), 'AutoCampaigns'),
    handle: {
      name: 'Automated Campaigns',
      analyticsEventName: 'view_marketing_automated_campaigns',
      key: SCOPES.AUTO_CAMPAIGNS,
    },
  },
  {
    path: 'one-time-campaigns',
    handle: {
      name: 'One-Time Campaigns',
      analyticsEventName: 'view_marketing_one_time_campaigns',
      key: SCOPES.ONE_TIME_CAMPAIGNS,
    },
    children: [
      {
        index: true,
        lazy: lazyRoute(
          () => import('containers/OneTimeCampaigns/OneTimeCampaigns'),
          'OneTimeCampaigns',
        ),
      },
      {
        path: ':campaignType/:campaignId',
        handle: {
          name: 'Campaign Details',
          analyticsEventName: 'view_marketing_campaign_details',
          key: SCOPES.ONE_TIME_CAMPAIGNS,
        },
        lazy: lazyRoute(
          () => import('containers/OneTimeCampaigns/CampaignDetails/CampaignDetails'),
          'CampaignDetails',
        ),
      },
    ],
  },
  {
    path: 'one-time-campaigns/reports',
    lazy: lazyRouteWithProps(
      () => import('containers/OneTimeCampaigns/OneTimeCampaigns'),
      'OneTimeCampaigns',
      { activeTab: 'reports' },
    ),
    handle: {
      name: 'One-Time Campaigns',
      analyticsEventName: 'view_marketing_one_time_campaigns',
      key: SCOPES.ONE_TIME_CAMPAIGNS,
    },
  },
  {
    path: 'auto-campaigns/reports',
    lazy: lazyRouteWithProps(
      () => import('containers/AutoCampaigns/AutoCampaigns'),
      'AutoCampaigns',
      { activeTab: 'reports' },
    ),
    handle: {
      name: 'Automated Campaigns',
      analyticsEventName: 'view_marketing_automated_campaigns',
      key: SCOPES.AUTO_CAMPAIGNS,
    },
  },
  {
    path: 'promos',
    lazy: lazyRoute(() => import('containers/Promos')),
    handle: {
      name: 'Promotions',
      analyticsEventName: 'view_marketing_promotions',
      key: SCOPES.PROMO_SETTINGS,
    },
  },
  {
    path: REWARDS_NAME_PLURAL,
    lazy: lazyRoute(() => import('containers/RewardsDetails/RewardsDetails')),
    handle: {
      name: `${capitalize(REWARDS_NAME_PLURAL)}`,
      analyticsEventName: 'view_location_rewards',
      key: SCOPES.MANAGE_REWARDS,
    },
  },
];

export const locationOrdersLinks = {
  path: 'orders',
  handle: {
    name: 'ChowNow Orders',
    analyticsEventName: 'view_orders_chownow',
    key: SCOPES.DIRECT_ORDERS,
    hideForHub: true,
  },
  children: [
    {
      index: true,
      lazy: lazyRoute(() => import('containers/OrdersList/OrdersListDirect')),
    },
    ...locationBasedOrderLinks.slice(1),
    ...orderDetailLinks,
  ],
};

// look for the index route in locationSettingsLinks (locationDetails)
const locationDetailsIndexRoute = locationSettingsLinks.find(link => link.index);

// steps with no page of their own yet fall back to the placeholder
const onboardingStepLoaders = {
  'menu-builder': lazyRoute(
    () => import('containers/OnboardingWizard/MenuBuilder'),
    'MenuBuilderStep',
  ),
  'brand-assets': lazyRoute(
    () => import('containers/OnboardingWizard/BrandAssets/BrandAssetsStep'),
    'BrandAssetsStep',
  ),
  delivery: lazyRoute(
    () => import('containers/OnboardingWizard/Delivery/DeliveryStep'),
    'DeliveryStep',
  ),
  'order-intake': lazyRoute(
    () => import('containers/OnboardingWizard/Orders/OrdersStep'),
    'OrdersStep',
  ),
  'payment-setup': lazyRoute(
    () => import('containers/OnboardingWizard/PaymentSetup/PaymentSetupStep'),
    'PaymentSetupStep',
  ),
  'point-of-sale': lazyRoute(
    () => import('containers/OnboardingWizard/PointOfSale/PointOfSaleStep'),
    'PointOfSaleStep',
  ),
  'sales-tax': lazyRoute(
    () => import('containers/OnboardingWizard/SalesTax/SalesTaxStep'),
    'SalesTaxStep',
  ),
  'website-setup': lazyRoute(
    () => import('containers/OnboardingWizard/WebsiteSetup/WebsiteSetupStep'),
    'WebsiteSetupStep',
  ),
};

const onboardingLinks = ONBOARDING_STEPS.map(step => ({
  path: step.id,
  lazy:
    onboardingStepLoaders[step.id] ??
    lazyRouteWithProps(
      () => import('containers/OnboardingWizard/OnboardingStepPlaceholder'),
      'OnboardingStepPlaceholder',
      { step },
    ),
  handle: {
    analyticsEventName: step.analyticsEventName,
    analyticsAttributes: () => ({
      source: ONBOARDING_SOURCE,
      step_name: step.analyticsStepName,
    }),
  },
}));

const getStartedLinks = GET_STARTED_SCREENS.map(screen => ({
  path: screen.path,
  lazy: lazyRouteWithProps(
    () => import('containers/GetStarted/GetStartedPlaceholder'),
    'GetStartedPlaceholder',
    { screenKey: screen.screenKey },
  ),
  handle: {
    analyticsEventName: screen.analyticsEventName,
    analyticsAttributes: () => ({ source: ONBOARDING_SOURCE }),
  },
}));

export const router = createBrowserRouter([
  {
    element: <Layout />,
    // Add logic here if we need to revalidate on url changes
    shouldRevalidate: () => false,
    errorElement: <ErrorPage buttonCta={ERROR_PAGE_BUTTONS.goToOverview} />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: '/login',
            lazy: lazyRoute(() => import('containers/UserAuth/Login/Login'), 'Login'),
            handle: { analyticsEventName: 'view_login' },
          },
          {
            path: '/logout',
            element: <Navigate to="/login" replace />,
            handle: { analyticsEventName: 'view_logout' },
          },
          {
            path: '/forgot-password',
            lazy: lazyRoute(
              () => import('containers/UserAuth/ForgotPassword/ForgotPassword'),
              'ForgotPassword',
            ),
            handle: { analyticsEventName: 'view_forgot_password' },
          },
          {
            path: '/reset-password',
            lazy: lazyRoute(() => import('containers/UserAuth/ResetPassword/ResetPassword')),
            handle: { analyticsEventName: 'view_reset_password' },
          },
          {
            path: '/activate-account',
            lazy: lazyRoute(
              () => import('containers/UserAuth/ActivateAccount/ActivateAccount'),
              'ActivateAccount',
            ),
            handle: { analyticsEventName: 'view_activate_account' },
          },
          {
            path: '/activate-account/check-email',
            lazy: lazyRoute(
              () =>
                import('containers/UserAuth/ActivateAccount/CheckEmail/ActivateAccountCheckEmail'),
              'ActivateAccountCheckEmail',
            ),
            handle: {
              analyticsEventName: 'view_activate_account_check_email',
              analyticsAttributes: () => ({ source: ONBOARDING_SOURCE }),
            },
          },
          {
            path: '/activate-account/link-expired',
            lazy: lazyRoute(
              () =>
                import('containers/UserAuth/ActivateAccount/LinkExpired/ActivateAccountLinkExpired'),
              'ActivateAccountLinkExpired',
            ),
            handle: {
              analyticsEventName: 'view_activate_account_link_expired',
              analyticsAttributes: () => ({ source: ONBOARDING_SOURCE }),
            },
          },
          {
            path: '/activate-account-credentials',
            lazy: lazyRoute(
              () =>
                import('containers/UserAuth/ActivateAccountCredentials/ActivateAccountCredentials'),
              'ActivateAccountCredentials',
            ),
            handle: {
              analyticsEventName: 'view_activate_account_credentials',
              analyticsAttributes: ({ searchParams, flags }) => {
                const isSelfServeOnboarding = isSelfServeOnboardingLink(searchParams, flags);

                return {
                  is_self_serve_onboarding: isSelfServeOnboarding,
                  ...(isSelfServeOnboarding && { source: ONBOARDING_SOURCE }),
                };
              },
            },
          },
          {
            path: '/set-corpa-user-impersonation',
            lazy: lazyRoute(() => import('components/Cuboh/SetCorpaUserImpersonation')),
          },
        ],
      },
      {
        element: <ProtectedRouteWrapper />,
        children: [
          {
            path: `${ONBOARDING_PATH}/*`,
            lazy: lazyRoute(
              () => import('containers/OnboardingWizard/OnboardingRedirect'),
              'OnboardingRedirect',
            ),
          },
          {
            // TODO(CN-50896): add the provisioning gate beside OnboardingWizardLayout's flag check.
            path: ONBOARDING_ROUTE,
            lazy: lazyRoute(
              () => import('containers/OnboardingWizard/OnboardingWizardLayout'),
              'OnboardingWizardLayout',
            ),
            handle: { ownsRouteLoader: true },
            children: [
              {
                index: true,
                lazy: lazyRoute(
                  () =>
                    import('containers/OnboardingWizard/LandingContent/OnboardingLandingContent'),
                  'OnboardingLandingContent',
                ),
              },
              ...onboardingLinks,
              {
                path: 'point-of-sale/oauth/:app/:status',
                lazy: lazyRoute(
                  () => import('containers/OnboardingWizard/PointOfSale'),
                  'PosLocationSelect',
                ),
              },
              {
                path: 'point-of-sale/oauth/result/:app/:status',
                lazy: lazyRoute(
                  () => import('containers/OnboardingWizard/PointOfSale'),
                  'PosOauthResult',
                ),
              },
            ],
          },
          {
            path: GET_STARTED_PATH,
            lazy: lazyRoute(
              () => import('containers/GetStarted/GetStartedLayout'),
              'GetStartedLayout',
            ),
            children: [
              { index: true, element: <Navigate to={FIRST_GET_STARTED_SCREEN.path} replace /> },
              ...getStartedLinks,
            ],
          },
        ],
      },
      {
        element: <OnboardingLockIn />,
        children: [
          {
            element: <ProtectedRouteWrapper />,
            children: [
              {
                path: '/restaurant/:restaurantId/one-time-campaigns/create',
                lazy: lazyRoute(
                  () =>
                    import('containers/OneTimeCampaigns/OneTimeCampaignWizard/OneTimeCampaignWizard'),
                  'OneTimeCampaignWizard',
                ),
                handle: {
                  name: 'Create Campaign',
                  analyticsEventName: 'view_marketing_create_campaign',
                  key: SCOPES.ONE_TIME_CAMPAIGNS,
                },
              },
              {
                path: '/restaurant/:restaurantId/campaign-insights/campaign-settings/csv-import',
                lazy: lazyRoute(
                  () => import('containers/CampaignSettings/CsvImportWizard/CsvImportWizard'),
                  'CsvImportWizard',
                ),
                handle: { name: 'CSV Import' },
              },
              {
                path: '/restaurant/:restaurantId/campaign-insights/campaign-settings/sms-registration',
                lazy: lazyRoute(
                  () =>
                    import('containers/CampaignSettings/SmsRegistrationWizard/SmsRegistrationWizard'),
                  'SmsRegistrationWizard',
                ),
                handle: { name: 'SMS Registration' },
              },
            ],
          },
          {
            element: <ProtectedRouteWrapper allowCuboh />,
            children: [
              {
                path: '/restaurant/:restaurantId/menu/upload',
                lazy: lazyRoute(
                  () => import('containers/MenuUpload/MenuUpload'),
                  'MenuUploadGuard',
                ),
                handle: { name: 'Upload Menu', analyticsEventName: 'view_menu_upload' },
              },
            ],
          },
          {
            id: PROTECTED_LAYOUT_ROUTE_ID,
            element: <ProtectedLayout />,
            children: [
              {
                path: '*',
                element: (
                  <ErrorPage
                    title={ERROR_PAGE_TITLES.notFound}
                    subtitle={ERROR_PAGE_SUBTITLES.notFound}
                    buttonCta={ERROR_PAGE_BUTTONS.startExploring}
                  />
                ),
              },
              {
                path: REWARDS_NAME_PLURAL,
                element: <ErrorPage />,
                handle: { name: `Manage ${REWARDS_NAME_PLURAL}`, isLinkOut: true },
              },
              {
                path: `${REWARDS_NAME_PLURAL}/*`,
                element: <ErrorPage />,
                handle: { name: `Manage ${REWARDS_NAME_PLURAL}`, isLinkOut: true },
              },
              {
                path: `${REWARDS_NAME_PLURAL}/:restaurantId`,
                lazy: lazyRouteWithProps(
                  () => import('containers/RewardsDetails/RewardsDetails'),
                  'default',
                  { isLinkOut: true },
                ),
                handle: { name: `Manage ${REWARDS_NAME_PLURAL}`, isLinkOut: true },
              },
              {
                path: '/unauthorized',
                lazy: lazyRouteWithProps(
                  () => import('components/Errors/ErrorCard/ErrorCardByType'),
                  'default',
                  { customStyles: { marginTop: dimension.xl6 } },
                ),
              },
              {
                path: '/',
                lazy: lazyRoute(() => import('containers/Home/Home')),
                handle: { name: 'Home', analyticsEventName: 'view_home', key: SCOPES.HOME },
              },
              {
                path: '/customer',
                children: [
                  {
                    path: ':customerId',
                    lazy: lazyRoute(() => import('containers/CustomerDetails/CustomerDetails')),
                  },
                ],
              },
              // We want to support both /preferredpartner and /preferredpartner/
              {
                path: '/preferredpartner',
                lazy: lazyRoute(
                  () => import('containers/PreferredByBusiness/PreferredByBusiness'),
                  'PreferredByBusiness',
                ),
                handle: {
                  name: 'Google Preferred Business',
                  analyticsEventName: 'view_admin_google_preferred_business',
                },
                children: [
                  {
                    path: '',
                    lazy: lazyRoute(
                      () => import('containers/PreferredByBusiness/PreferredByBusiness'),
                      'PreferredByBusiness',
                    ),
                  },
                ],
              },
              {
                path: '/preferredpartner',
                lazy: lazyRoute(
                  () => import('containers/PreferredByBusiness/PreferredByBusiness'),
                  'PreferredByBusiness',
                ),
                handle: {
                  name: 'Google Preferred Business',
                  analyticsEventName: 'view_admin_google_preferred_business',
                },
              },
              {
                // have to add slash here since absolute path not allowed when nesting under locations
                path: `/${REPORTS_PATH}`,
                children: [
                  { index: true, element: <Navigate to="analytics" replace /> },
                  ...reportingLinks,
                  ...cubohReportingLinks,
                ],
              },
              // Need these redirects because we can't do dynamic redirects within the 3P services
              {
                path: '/integrations/:action/:app',
                lazy: lazyRoute(() => import('components/Integrations/IntegrationsRedirect')),
              },
              {
                path: '/integrations/pos/oauth/:action/:app',
                lazy: lazyRoute(() => import('components/Integrations/IntegrationsRedirect')),
              },
              {
                path: '/restaurant/:restaurantId',
                lazy: lazyRoute(() => import('layouts/LocationDetailsLayout')),
                children: [
                  {
                    path: LOCATION_HOME_PATH,
                    lazy: lazyRoute(() => import('containers/PreLiveHome'), 'LocationHomeGate'),
                    handle: {
                      name: 'Home',
                      analyticsEventName: 'view_location_home',
                      key: SCOPES.LOCATION_HOME,
                    },
                  },
                  locationDetailsIndexRoute,
                  ...integrationsLinks,
                  ...marketingLinks,
                  ...menuLinks,
                  {
                    path: 'settings',
                    children: [
                      ...locationSettingsLinks,
                      {
                        path: LOCATION_SETTINGS.ORDERING,
                        handle: { parent: 'Location Details' },
                        lazy: locationDetailsRoute(LOCATION_SETTINGS.ORDERING),
                      },
                      {
                        path: LOCATION_SETTINGS.CATERING,
                        handle: { parent: 'Location Details' },
                        lazy: locationDetailsRoute(LOCATION_SETTINGS.CATERING),
                      },
                      {
                        path: LOCATION_SETTINGS.DELIVERY,
                        handle: { parent: 'Location Details' },
                        lazy: locationDetailsRoute(LOCATION_SETTINGS.DELIVERY),
                      },
                    ],
                  },
                  locationOrdersLinks,
                  {
                    path: SCOPES.OVERVIEW,
                    lazy: lazyRoute(
                      () => import('containers/Home/LocationOverviewRoute'),
                      'LocationOverviewRoute',
                    ),
                    handle: {
                      name: 'Overview',
                      analyticsEventName: 'view_location_overview',
                      key: SCOPES.OVERVIEW,
                    },
                  },
                  {
                    path: SCOPES.PROGRESS,
                    lazy: lazyRoute(
                      () => import('containers/LocationGoLiveChecklist/LocationGoLiveChecklist'),
                    ),
                    handle: {
                      name: GO_LIVE_COVER_TITLE,
                      analyticsEventName: 'view_go_live_checklist',
                      key: SCOPES.PROGRESS,
                    },
                  },
                  {
                    path: REPORTS_PATH,
                    children: [
                      {
                        index: true,
                        lazy: lazyRouteWithProps(
                          () => import('components/ReportsIndexRedirect'),
                          'ReportsIndexRedirect',
                          { links: locationReportingLinks },
                        ),
                      },
                      {
                        path: CHANNEL_INSIGHTS_PATH,
                        lazy: lazyRoute(() => import('containers/Home/Home')),
                        handle: {
                          name: 'Channel Insights',
                          analyticsEventName: 'view_reports_channel_insights',
                          key: SCOPES.CHANNEL_INSIGHTS,
                        },
                      },
                      ...reportingLinks,
                      ...cubohReportingLinks,
                      ...cnOnlyReportingLinks,
                    ],
                  },
                  {
                    path: TRANSACTION_FEES_PATH,
                    handle: {
                      name: 'Transaction Fees',
                      analyticsEventName: 'view_location_transaction_fees',
                      key: SCOPES.PLATFORM_FEES,
                    },
                    lazy: lazyRoute(() => import('containers/TransactionFees/TransactionFees')),
                  },
                  {
                    path: 'channel-manager',
                    handle: {
                      name: 'Channel Manager',
                      analyticsEventName: 'view_channel_manager',
                      key: SCOPES.CHANNEL_MANAGER,
                    },
                    children: [
                      {
                        index: true,
                        element: <Navigate to={CHANNEL_MANAGER_TABS.insights} replace />,
                      },
                      {
                        path: CHANNEL_MANAGER_TABS.insights,
                        lazy: channelManagerRoute(CHANNEL_MANAGER_TABS.insights),
                      },
                      {
                        path: CHANNEL_MANAGER_TABS.manage,
                        lazy: channelManagerRoute(CHANNEL_MANAGER_TABS.manage),
                      },
                    ],
                  },
                  {
                    path: '*',
                    lazy: lazyRouteWithProps(
                      () => import('components/LegacyRedirect/LegacyRedirect'),
                      'default',
                      {
                        type: 'restaurant',
                        paramName: 'restaurantId',
                        getRedirectPath: restaurantId => `/restaurant/${restaurantId}`,
                      },
                    ),
                  },
                ],
              },
              {
                path: '/company/:companyId',
                children: [
                  ...companyLinks,
                  {
                    path: '*',
                    lazy: lazyRouteWithProps(
                      () => import('components/LegacyRedirect/LegacyRedirect'),
                      'default',
                      {
                        type: 'company',
                        paramName: 'companyId',
                        getRedirectPath: getCompanyRedirectPath,
                      },
                    ),
                  },
                ],
              },
              {
                path: '/account',
                children: [
                  {
                    path: 'settings',
                    lazy: lazyRoute(() => import('components/Profile/Settings')),
                    handle: { name: 'Settings', analyticsEventName: 'view_admin_settings' },
                  },
                  {
                    path: 'invoice',
                    lazy: lazyRoute(() => import('components/Profile/Invoices')),
                    handle: { name: 'Invoices', analyticsEventName: 'view_admin_invoices' },
                  },
                ],
              },
              {
                path: `${INTERNAL_PATH}`,
                lazy: lazyRoute(() => import('layouts/InternalLayout')),
                children: [
                  {
                    path: 'restaurants',
                    lazy: lazyRoute(
                      () => import('components/AllRestaurants/AllRestaurants'),
                      'AllRestaurants',
                    ),
                    handle: {
                      name: 'All Restaurants',
                      analyticsEventName: 'view_hq_all_restaurants',
                    },
                  },
                  {
                    path: 'companies',
                    lazy: lazyRoute(() => import('components/AllRestaurants/AllHeadquarters')),
                    handle: {
                      name: 'All Restaurants',
                      analyticsEventName: 'view_hq_all_restaurants',
                    },
                  },
                  {
                    path: 'orders',
                    lazy: embeddedAdminRoute({ src: 'orders' }),
                    handle: { name: 'All Orders', analyticsEventName: 'view_orders_all' },
                  },
                  {
                    path: 'customers',
                    lazy: embeddedAdminRoute({ src: 'customers' }),
                    handle: { name: 'All Customers', analyticsEventName: 'view_customers_all' },
                  },
                  {
                    path: 'cuisines',
                    lazy: lazyRoute(
                      () => import('containers/CuisineCategories/CuisineCategoriesPage'),
                      'CuisineCategoriesPage',
                    ),
                    handle: {
                      name: 'Cuisine Categories',
                      analyticsEventName: 'view_admin_cuisines',
                    },
                  },
                  {
                    path: 'push-notifications',
                    lazy: lazyRoute(
                      () => import('containers/AdminPushNotifications/AdminPushNotificationsPage'),
                      'AdminPushNotificationsPage',
                    ),
                    handle: {
                      name: 'Push Notifications',
                      analyticsEventName: 'view_admin_push_notifications',
                      hideGlobalHqName: true,
                    },
                  },
                  {
                    path: 'users',
                    lazy: embeddedAdminRoute({ src: 'users' }),
                    handle: { name: 'Admin Users', analyticsEventName: 'view_admin_users' },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
